import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaShieldAlt,
  FaDatabase,
  FaCookieBite,
  FaUserSecret,
  FaLock,
  FaGlobe,
  FaFileContract,
  FaGavel,
  FaEye,
  FaTrash,
  FaBell,
  FaChild,
  FaChartLine,
} from "react-icons/fa";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const handleGoBack = () => navigate(-1);
  const handleContact = () => navigate("/contact");
  const handleTermsOfUse = () => navigate("/terms-of-use");
  const handlePrivacy = () => navigate("/privacy");

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-3 py-3 md:px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center shrink-0"
            >
              <img
                src="/VisionRoyale_logo.png"
                alt="Vision Royale"
                className="h-8 w-auto md:h-12 object-contain"
              />
            </button>

            {/* Buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={handleGoBack}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/20 bg-purple-400/10 px-3 py-1.5 sm:px-4 md:px-5 md:py-2 text-[10px] sm:text-sm font-semibold text-purple-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-600/30"
              >
                <FaArrowLeft className="text-xs" />
                Back
              </button>
              <button
                onClick={handleContact}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/20 bg-green-400/10 px-3 py-1.5 sm:px-4 md:px-5 md:py-2 text-[10px] sm:text-sm font-semibold text-green-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-600/30"
              >
                <FaEnvelope />
                Contact
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-12 lg:py-16">
        {/* Hero Section */}
        <div className="mb-8 md:mb-12 text-center">
          <div className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm font-semibold text-emerald-300 mb-4">
            <FaShieldAlt className="text-xs" />
            <span>Privacy & Data Protection</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Privacy{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
              Policy
            </span>
          </h1>
          <p className="mt-4 text-white/60 text-sm md:text-base">
            Last Updated: May 16, 2026
          </p>
        </div>

        {/* Privacy Content */}
        <div className="space-y-6 md:space-y-8">
          {/* Introduction */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <p className="text-white/80 text-sm md:text-base leading-relaxed">
              Welcome to{" "}
              <span className="text-emerald-400 font-semibold">
                Vision Royale
              </span>
              . Your privacy is important to us.
            </p>
            <p className="mt-3 text-white/70 text-sm md:text-base leading-relaxed">
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our Service. Please read
              this Privacy Policy carefully. By using the Service, you consent
              to the data practices described in this policy.
            </p>
          </div>

          {/* 1. Information We Collect */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-400/10 text-blue-400">
                <FaDatabase />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                1. Information We Collect
              </h2>
            </div>
            <div className="space-y-4 pl-0 md:pl-11">
              <div>
                <h3 className="text-white font-semibold text-base mb-2">
                  1.1 Personal Data
                </h3>
                <p className="text-white/60 text-sm">
                  When you create an account, we may collect:
                </p>
                <ul className="mt-2 space-y-1 text-white/60 text-sm list-disc list-inside">
                  <li>Email address</li>
                  <li>Username</li>
                  <li>Password (encrypted)</li>
                  <li>Subscription preferences</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base mb-2">
                  1.2 Usage Data
                </h3>
                <p className="text-white/60 text-sm">
                  We automatically collect information about your interaction
                  with the Service, including:
                </p>
                <ul className="mt-2 space-y-1 text-white/60 text-sm list-disc list-inside">
                  <li>Pages visited and features used</li>
                  <li>Time spent on the platform</li>
                  <li>Projects created and analyses viewed</li>
                  <li>Device information (browser type, OS, IP address)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base mb-2">
                  1.3 Payment Information
                </h3>
                <p className="text-white/60 text-sm">
                  All payments are processed through Stripe. We do not store
                  credit card details on our servers. Stripe collects and
                  processes payment information in accordance with their privacy
                  policy and PCI compliance standards.
                </p>
              </div>
            </div>
          </div>

          {/* 2. How We Use Your Information */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-400/10 text-purple-400">
                <FaChartLine />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                2. How We Use Your Information
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm mb-3">
                We use the information we collect to:
              </p>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>Provide, maintain, and improve the Service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>Process your subscription payments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>Send technical notices and support messages</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>Analyze usage patterns to enhance user experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>
                    Respond to your inquiries and provide customer support
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>Comply with legal obligations</span>
                </li>
              </ul>
            </div>
          </div>

          {/* 3. Cookies and Tracking Technologies */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-yellow-400/10 text-yellow-400">
                <FaCookieBite />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                3. Cookies and Tracking Technologies
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                We use cookies and similar tracking technologies to track
                activity on our Service and store certain information. Cookies
                are files with a small amount of data. You can instruct your
                browser to refuse all cookies or to indicate when a cookie is
                being sent. However, if you do not accept cookies, you may not
                be able to use some portions of our Service.
              </p>
            </div>
          </div>

          {/* 4. Data Retention */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-400/10 text-orange-400">
                <FaTrash />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                4. Data Retention
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                We will retain your personal data only for as long as is
                necessary for the purposes set out in this Privacy Policy. We
                will retain and use your data to the extent necessary to comply
                with our legal obligations, resolve disputes, and enforce our
                agreements.
              </p>
              <p className="text-white/60 text-sm mt-3">
                If you delete your account, we will delete or anonymize your
                personal data within 30 days, except where we are required to
                retain it for legal compliance purposes.
              </p>
            </div>
          </div>

          {/* 5. Data Security */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
                <FaLock />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                5. Data Security
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                We implement appropriate technical and organizational measures
                to protect your personal data against unauthorized access,
                alteration, disclosure, or destruction. These include:
              </p>
              <ul className="mt-3 space-y-1 text-white/60 text-sm list-disc list-inside">
                <li>Encryption of data in transit (TLS/SSL)</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Regular security assessments</li>
                <li>Access controls and authentication measures</li>
                <li>Secure payment processing through Stripe</li>
              </ul>
              <p className="text-white/60 text-sm mt-3">
                However, no method of transmission over the Internet or method
                of electronic storage is 100% secure. While we strive to use
                commercially acceptable means to protect your personal data, we
                cannot guarantee its absolute security.
              </p>
            </div>
          </div>

          {/* 6. Sharing Your Information */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-400">
                <FaGlobe />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                6. Sharing Your Information
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                We do not sell, trade, or rent your personal data to third
                parties. We may share your information in the following
                circumstances:
              </p>
              <ul className="mt-3 space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>
                    <strong>Service Providers:</strong> We may share data with
                    third-party vendors who perform services on our behalf
                    (payment processing, analytics, hosting).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>
                    <strong>Legal Requirements:</strong> We may disclose your
                    information if required by law or in response to valid
                    requests by public authorities.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>
                    <strong>Business Transfers:</strong> In the event of a
                    merger, acquisition, or asset sale, your data may be
                    transferred.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* 7. Your Rights */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-400/10 text-indigo-400">
                <FaUserSecret />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                7. Your Rights (GDPR & CCPA)
              </h2>
            </div>
            <div className="pl-0 md:pl-11 space-y-3">
              <p className="text-white/60 text-sm">
                Depending on your location, you may have the following rights
                regarding your personal data:
              </p>
              <ul className="space-y-1 text-white/60 text-sm list-disc list-inside">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure (“right to be forgotten”)</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent at any time</li>
              </ul>
              <p className="text-white/60 text-sm mt-3">
                To exercise any of these rights, please contact us through the
                Contact page. We will respond to your request within 30 days.
              </p>
            </div>
          </div>

          {/* 8. Children's Privacy */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-pink-400/10 text-pink-400">
                <FaChild />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                8. Children's Privacy
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                Our Service is not intended for individuals under 18 years of
                age. We do not knowingly collect personal information from
                children under 18. If you become aware that a child has provided
                us with personal data, please contact us immediately.
              </p>
            </div>
          </div>

          {/* 9. Third-Party Links */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-teal-400/10 text-teal-400">
                <FaGlobe />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                9. Third-Party Links
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                Our Service may contain links to third-party websites or
                services that are not owned or controlled by us. We have no
                control over and assume no responsibility for the content,
                privacy policies, or practices of any third-party websites.
              </p>
            </div>
          </div>

          {/* 10. International Data Transfers */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-400/10 text-blue-400">
                <FaGlobe />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                10. International Data Transfers
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                Your information may be transferred to and maintained on
                computers located outside of your state, province, country, or
                other governmental jurisdiction where data protection laws may
                differ. By using the Service, you consent to the transfer of
                your data to Romania and other jurisdictions as necessary for
                providing the Service.
              </p>
            </div>
          </div>

          {/* 11. Updates to This Policy */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-400/10 text-amber-400">
                <FaBell />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                11. Updates to This Privacy Policy
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                We may update our Privacy Policy from time to time. We will
                notify you of any changes by posting the new Privacy Policy on
                this page and updating the "Last Updated" date. You are advised
                to review this Privacy Policy periodically for any changes.
                Changes to this Privacy Policy are effective when they are
                posted on this page.
              </p>
            </div>
          </div>

          {/* 12. Contact Information */}
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
                <FaEnvelope />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                12. Contact Us
              </h2>
            </div>
            <div className="pl-0 md:pl-11">
              <p className="text-white/60 text-sm">
                If you have any questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <ul className="mt-3 space-y-1 text-white/60 text-sm">
                <li>• Through the Contact page on our website</li>
                <li>• Via email through the contact form</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 md:mt-12 rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 text-center">
          <p className="text-white/50 text-xs md:text-sm">
            By using Vision Royale, you acknowledge that you have read,
            understood, and agree to this Privacy Policy.
          </p>
          <p className="text-white/40 text-xs mt-2">
            Vision Royale — AI-Powered Value Edge Engine Platform
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-slate-950/80">
        <div className="mx-auto max-w-7xl px-4 py-6 md:py-8 md:px-6">
          <div className="flex flex-col items-center justify-between gap-3 md:gap-4 md:flex-row">
            <div className="text-center text-xs text-white/40 md:text-left">
              © 2026 Vision Royale. AI-Powered Value Edge Engine Platform.
            </div>
            <div className="flex gap-5 md:gap-6 text-xs md:text-sm text-white/50">
              <button
                onClick={handleTermsOfUse}
                className="hover:text-white transition-colors"
              >
                Terms Of Use
              </button>
              <button
                onClick={handlePrivacy}
                className="hover:text-white transition-colors"
              >
                Privacy
              </button>
              <button
                onClick={handleContact}
                className="hover:text-white transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
